import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import supabase from '../lib/supabase';

const ProjectContext = createContext();

export const useProject = () => {
  const context = useContext(ProjectContext);
  if (!context) {
    throw new Error('useProject must be used within a ProjectProvider');
  }
  return context;
};

export const ProjectProvider = ({ children }) => {
  const { user, loading: authLoading, authInitialized } = useAuth();
  const [currentProject, setCurrentProject] = useState(null);
  const [files, setFiles] = useState({});
  const [activeFile, setActiveFile] = useState(null);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [loadingTimeout, setLoadingTimeout] = useState(null);
  const [projectsLoaded, setProjectsLoaded] = useState(false);

  // Only load projects when auth is initialized and state changes
  useEffect(() => {
    if (!authInitialized) {
      console.log('⏳ ProjectContext: Waiting for auth initialization...');
      return;
    }

    if (user && !projectsLoaded) {
      console.log('👤 ProjectContext: User detected, loading projects...');
      loadProjectsWithTimeout();
    } else if (!user && authInitialized) {
      console.log('🚪 ProjectContext: No user, loading local projects...');
      // User signed out or not authenticated, load from localStorage
      loadProjectsFromLocal();
      // Clear cloud-synced data but keep the projects loaded from localStorage
      setCurrentProject(null);
      setFiles({});
      setActiveFile(null);
      setProjectsLoaded(true);
    }
  }, [user, authInitialized, projectsLoaded]);

  // Reset projects loaded flag when user changes
  useEffect(() => {
    setProjectsLoaded(false);
  }, [user]);

  const loadProjectsWithTimeout = async () => {
    // Prevent multiple simultaneous loads
    if (loading) {
      console.log('⚠️ ProjectContext: Already loading, skipping...');
      return;
    }

    // Clear any existing timeout
    if (loadingTimeout) {
      clearTimeout(loadingTimeout);
    }

    // Set a timeout to prevent infinite loading
    const timeout = setTimeout(() => {
      console.warn('⚠️ Loading projects timeout, falling back to localStorage');
      setLoading(false);
      loadProjectsFromLocal();
      setProjectsLoaded(true);
    }, 8000); // 8 second timeout

    setLoadingTimeout(timeout);

    try {
      await loadProjects();
      clearTimeout(timeout);
      setProjectsLoaded(true);
    } catch (error) {
      clearTimeout(timeout);
      console.error('❌ Error in loadProjectsWithTimeout:', error);
      setLoading(false);
      loadProjectsFromLocal();
      setProjectsLoaded(true);
    }
  };

  const loadProjects = async () => {
    if (!user) {
      console.log('❌ No user found, cannot load from Supabase');
      return;
    }

    try {
      setLoading(true);
      console.log('📥 Loading projects from Supabase for user:', user.email);

      // Add a race condition protection with AbortController
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 6000); // 6 second timeout

      const { data, error } = await supabase
        .from('users_projects_ac2025')
        .select('*')
        .eq('user_id', user.id)
        .order('updated_at', { ascending: false })
        .abortSignal(controller.signal);

      clearTimeout(timeoutId);

      if (error) {
        if (error.name === 'AbortError') {
          throw new Error('Request timeout - falling back to local storage');
        }
        throw error;
      }

      const formattedProjects = data.map(project => ({
        id: project.project_id,
        name: project.name,
        template: project.template,
        files: project.files || {},
        createdAt: project.created_at,
        updatedAt: project.updated_at
      }));

      setProjects(formattedProjects);
      console.log('✅ Projects loaded from Supabase:', formattedProjects.length);

      // Check for local projects to sync
      const localProjects = loadProjectsFromLocal(false);
      if (localProjects && localProjects.length > 0) {
        console.log('📱 Found local projects, syncing to cloud...');
        await syncLocalProjectsToCloud(localProjects);
      }
    } catch (error) {
      console.error('❌ Error loading projects from Supabase:', error);
      // Fallback to localStorage for offline usage
      loadProjectsFromLocal();
    } finally {
      setLoading(false);
    }
  };

  const loadProjectsFromLocal = (setInState = true) => {
    try {
      const savedProjects = localStorage.getItem('codeEditor_projects');
      if (savedProjects) {
        const localProjects = JSON.parse(savedProjects);
        if (setInState) {
          setProjects(localProjects);
          console.log('📱 Projects loaded from localStorage (offline):', localProjects.length);
        }
        return localProjects;
      }
    } catch (error) {
      console.error('❌ Error loading from localStorage:', error);
    }
    
    if (setInState) {
      setProjects([]);
    }
    return [];
  };

  const syncLocalProjectsToCloud = async (localProjects) => {
    if (!user || !localProjects || localProjects.length === 0) return;

    try {
      setSyncing(true);
      console.log('🔄 Syncing local projects to cloud...');

      for (const project of localProjects) {
        await saveProjectToSupabase(project, false);
      }

      // Clear local storage after successful sync
      localStorage.removeItem('codeEditor_projects');
      console.log('✅ Local projects synced to cloud and cleared from localStorage');

      // Reload projects from Supabase
      await loadProjects();
    } catch (error) {
      console.error('❌ Error syncing local projects:', error);
    } finally {
      setSyncing(false);
    }
  };

  const saveProjectToSupabase = async (project, isUpdate = false) => {
    if (!user) {
      // Save to localStorage if not authenticated
      saveProjectToLocal(project);
      return null;
    }

    try {
      setSyncing(true);
      const projectData = {
        user_id: user.id,
        project_id: project.id,
        name: project.name,
        template: project.template,
        files: project.files || {}
      };

      let result;
      if (isUpdate) {
        const { data, error } = await supabase
          .from('users_projects_ac2025')
          .update(projectData)
          .eq('user_id', user.id)
          .eq('project_id', project.id)
          .select()
          .single();

        if (error) throw error;
        result = data;
      } else {
        const { data, error } = await supabase
          .from('users_projects_ac2025')
          .upsert(projectData, {
            onConflict: 'user_id,project_id',
            ignoreDuplicates: false
          })
          .select()
          .single();

        if (error) throw error;
        result = data;
      }

      console.log('✅ Project saved to Supabase:', project.name);
      return result;
    } catch (error) {
      console.error('❌ Error saving project to Supabase:', error);
      // Fallback to localStorage
      saveProjectToLocal(project);
      return null;
    } finally {
      setSyncing(false);
    }
  };

  const saveProjectToLocal = (project) => {
    try {
      const savedProjects = localStorage.getItem('codeEditor_projects');
      let localProjects = savedProjects ? JSON.parse(savedProjects) : [];

      const existingIndex = localProjects.findIndex(p => p.id === project.id);
      if (existingIndex !== -1) {
        localProjects[existingIndex] = project;
      } else {
        localProjects.push(project);
      }

      localStorage.setItem('codeEditor_projects', JSON.stringify(localProjects));
      console.log('📱 Project saved to localStorage (offline):', project.name);
    } catch (error) {
      console.error('❌ Error saving to localStorage:', error);
    }
  };

  const createProject = async (name, template = 'blank') => {
    const newProject = {
      id: Date.now().toString(),
      name,
      template,
      createdAt: new Date().toISOString(),
      files: getTemplateFiles(template)
    };

    try {
      // Save to Supabase or localStorage first
      await saveProjectToSupabase(newProject);

      // Update local state
      setProjects(prev => [newProject, ...prev]);
      setCurrentProject(newProject);
      setFiles(newProject.files);
      setActiveFile(Object.keys(newProject.files)[0] || null);

      console.log('✅ Project created successfully:', name);
      return newProject;
    } catch (error) {
      console.error('❌ Error creating project:', error);
      throw error;
    }
  };

  // Enhanced createProjectWithFiles for import functionality
  const createProjectWithFiles = async (name, template = 'blank', importedFiles = {}) => {
    const newProject = {
      id: Date.now().toString(),
      name,
      template,
      createdAt: new Date().toISOString(),
      files: Object.keys(importedFiles).length > 0 ? importedFiles : getTemplateFiles(template)
    };

    try {
      console.log('🚀 Creating project with files:', name, 'Files count:', Object.keys(newProject.files).length);

      // Save to Supabase or localStorage - WAIT for completion
      await saveProjectToSupabase(newProject);

      // Update local state
      setProjects(prev => [newProject, ...prev]);
      setCurrentProject(newProject);
      setFiles(newProject.files);
      setActiveFile(Object.keys(newProject.files)[0] || null);

      console.log('✅ Project with files created successfully:', name, 'Final files:', Object.keys(newProject.files));
      return newProject;
    } catch (error) {
      console.error('❌ Error creating project with files:', error);
      throw error;
    }
  };

  const duplicateProject = async (project) => {
    const duplicatedProject = {
      ...project,
      id: Date.now().toString(),
      name: `${project.name} (Copy)`,
      createdAt: new Date().toISOString(),
      files: { ...project.files }
    };

    try {
      // Save to Supabase or localStorage
      await saveProjectToSupabase(duplicatedProject);

      // Update local state
      setProjects(prev => [duplicatedProject, ...prev]);

      console.log('✅ Project duplicated successfully:', duplicatedProject.name);
      return duplicatedProject;
    } catch (error) {
      console.error('❌ Error duplicating project:', error);
      throw error;
    }
  };

  const loadProject = (project) => {
    setCurrentProject(project);
    setFiles(project.files);
    setActiveFile(Object.keys(project.files)[0] || null);
  };

  const renameProject = async (projectId, newName) => {
    const project = projects.find(p => p.id === projectId);
    if (!project) return;

    const updatedProject = { ...project, name: newName };

    try {
      // Save to Supabase or localStorage
      await saveProjectToSupabase(updatedProject, true);

      // Update local state
      const updatedProjects = projects.map(p =>
        p.id === projectId ? updatedProject : p
      );
      setProjects(updatedProjects);

      if (currentProject?.id === projectId) {
        setCurrentProject(updatedProject);
      }

      console.log('✅ Project renamed successfully:', newName);
    } catch (error) {
      console.error('❌ Error renaming project:', error);
      throw error;
    }
  };

  const saveCurrentProject = async () => {
    if (!currentProject) return;

    const updatedProject = {
      ...currentProject,
      files,
      updatedAt: new Date().toISOString()
    };

    try {
      // Save to Supabase or localStorage
      await saveProjectToSupabase(updatedProject, true);

      // Update local state
      const updatedProjects = projects.map(p =>
        p.id === currentProject.id ? updatedProject : p
      );
      setProjects(updatedProjects);
      setCurrentProject(updatedProject);

      console.log('💾 Project saved successfully');
    } catch (error) {
      console.error('❌ Error saving project:', error);
      throw error;
    }
  };

  const deleteProject = async (projectId) => {
    if (!user) {
      // Delete from localStorage if not authenticated
      const updatedProjects = projects.filter(p => p.id !== projectId);
      setProjects(updatedProjects);
      localStorage.setItem('codeEditor_projects', JSON.stringify(updatedProjects));

      if (currentProject?.id === projectId) {
        setCurrentProject(null);
        setFiles({});
        setActiveFile(null);
      }
      return;
    }

    try {
      setSyncing(true);
      const { error } = await supabase
        .from('users_projects_ac2025')
        .delete()
        .eq('user_id', user.id)
        .eq('project_id', projectId);

      if (error) throw error;

      // Update local state
      const updatedProjects = projects.filter(p => p.id !== projectId);
      setProjects(updatedProjects);

      if (currentProject?.id === projectId) {
        setCurrentProject(null);
        setFiles({});
        setActiveFile(null);
      }

      console.log('🗑️ Project deleted from Supabase');
    } catch (error) {
      console.error('❌ Error deleting project:', error);
      throw error;
    } finally {
      setSyncing(false);
    }
  };

  const createFile = (path, content = '') => {
    const newFiles = { ...files, [path]: content };
    setFiles(newFiles);
    setActiveFile(path);
  };

  const duplicateFile = (originalPath) => {
    if (!files[originalPath]) return;

    const pathParts = originalPath.split('/');
    const fileName = pathParts.pop();
    const directory = pathParts.length > 0 ? pathParts.join('/') + '/' : '';

    const lastDotIndex = fileName.lastIndexOf('.');
    let baseName, extension;
    if (lastDotIndex > 0) {
      baseName = fileName.substring(0, lastDotIndex);
      extension = fileName.substring(lastDotIndex);
    } else {
      baseName = fileName;
      extension = '';
    }

    let copyNumber = 1;
    let newFileName;
    let newPath;

    do {
      newFileName = `${baseName} (Copy${copyNumber > 1 ? ` ${copyNumber}` : ''})${extension}`;
      newPath = directory + newFileName;
      copyNumber++;
    } while (files[newPath]);

    const newFiles = { ...files, [newPath]: files[originalPath] };
    setFiles(newFiles);
    setActiveFile(newPath);
    return newPath;
  };

  const createFolder = (folderName) => {
    const folderPath = `${folderName}/.gitkeep`;
    const newFiles = { ...files, [folderPath]: '' };
    setFiles(newFiles);
  };

  const updateFile = (path, content) => {
    setFiles(prev => ({ ...prev, [path]: content }));
  };

  const renameFile = (oldPath, newPath) => {
    const newFiles = { ...files };
    const content = newFiles[oldPath];
    delete newFiles[oldPath];
    newFiles[newPath] = content;
    setFiles(newFiles);

    if (activeFile === oldPath) {
      setActiveFile(newPath);
    }
  };

  const moveFile = (sourcePath, targetPath) => {
    if (sourcePath === targetPath) return;

    const newFiles = { ...files };
    const content = newFiles[sourcePath];

    if (content !== undefined) {
      newFiles[targetPath] = content;
      delete newFiles[sourcePath];
      setFiles(newFiles);

      if (activeFile === sourcePath) {
        setActiveFile(targetPath);
      }
    }
  };

  const deleteFile = (path) => {
    const newFiles = { ...files };

    if (path.endsWith('/') || Object.keys(files).some(f => f.startsWith(path + '/'))) {
      // Delete folder and all its contents
      Object.keys(newFiles).forEach(filePath => {
        if (filePath.startsWith(path)) {
          delete newFiles[filePath];
        }
      });
    } else {
      // Delete single file
      delete newFiles[path];
    }

    setFiles(newFiles);

    if (activeFile === path || activeFile?.startsWith(path + '/')) {
      const remainingFiles = Object.keys(newFiles);
      setActiveFile(remainingFiles.length > 0 ? remainingFiles[0] : null);
    }
  };

  const getTemplateFiles = (template) => {
    switch (template) {
      case 'react':
        return {
          'index.html': `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>React App</title>
  <script src="https://unpkg.com/react@18/umd/react.development.js"></script>
  <script src="https://unpkg.com/react-dom@18/umd/react-dom.development.js"></script>
  <script src="https://unpkg.com/@babel/standalone/babel.min.js"></script>
</head>
<body>
  <div id="root"></div>
  <script type="text/babel" src="app.js"></script>
</body>
</html>`,
          'app.js': `const { useState } = React;

function App() {
  const [count, setCount] = useState(0);

  return (
    <div style={{ padding: '20px', fontFamily: 'Arial, sans-serif' }}>
      <h1>Hello React!</h1>
      <p>Count: {count}</p>
      <button onClick={() => setCount(count + 1)}>
        Increment
      </button>
    </div>
  );
}

ReactDOM.render(<App />, document.getElementById('root'));`,
          'style.css': `body {
  margin: 0;
  padding: 0;
  background-color: #f0f0f0;
}`
        };
      case 'vanilla':
        return {
          'index.html': `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Vanilla JS App</title>
  <link rel="stylesheet" href="style.css">
</head>
<body>
  <div id="app">
    <h1>Hello World!</h1>
    <button id="btn">Click me</button>
    <p id="output">Click count: 0</p>
  </div>
  <script src="script.js"></script>
</body>
</html>`,
          'script.js': `let count = 0;
const btn = document.getElementById('btn');
const output = document.getElementById('output');

btn.addEventListener('click', () => {
  count++;
  output.textContent = \`Click count: \${count}\`;
});`,
          'style.css': `body {
  font-family: Arial, sans-serif;
  margin: 0;
  padding: 20px;
  background-color: #f0f0f0;
}

#app {
  max-width: 600px;
  margin: 0 auto;
  background: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

button {
  background: #007bff;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 4px;
  cursor: pointer;
}

button:hover {
  background: #0056b3;
}`
        };
      default:
        return {
          'index.html': `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>New Project</title>
</head>
<body>
  <h1>Hello World!</h1>
</body>
</html>`
        };
    }
  };

  return (
    <ProjectContext.Provider
      value={{
        currentProject,
        files,
        activeFile,
        projects,
        loading,
        syncing,
        projectsLoaded,
        createProject,
        createProjectWithFiles,
        duplicateProject,
        loadProject,
        renameProject,
        saveCurrentProject,
        deleteProject,
        createFile,
        duplicateFile,
        createFolder,
        updateFile,
        renameFile,
        moveFile,
        deleteFile,
        setActiveFile
      }}
    >
      {children}
    </ProjectContext.Provider>
  );
};